import { axios } from "utilities/axios";
import { AxiosError } from "axios";
import { useQueries, useQuery, UseQueryOptions } from "react-query";
import { compareAsc } from "date-fns";
import {
    IInteractionsCountResponse,
    IInteractionsGetQueryParameters,
    IInteractionsCount,
    Route,
    TimePeriodGroupBy,
} from "@edgetier/watchtower-types";
import { useTimeZone } from "hooks-for/time-zones/use-time-zone";
import { isValueEmpty } from "utilities/is-value-empty";
import { doNothing } from "@edgetier/utilities";

type IGroupType<Parameters extends IInteractionsGetQueryParameters> = Parameters["groupBy"] extends undefined
    ? null
    : Parameters["groupBy"] extends TimePeriodGroupBy
    ? Date
    : string;

/**
 * Count the number of interactions matching some conditions.
 * @param parameters Route filters.
 * @param url URL to get counts.
 */
const getInteractionsCounts = async <Parameters extends IInteractionsGetQueryParameters>(
    url: string,
    parameters: Parameters
) => {
    const response = await axios.get<IInteractionsCountResponse<IGroupType<Parameters>>>(url, { params: parameters });

    // Sort by date if necessary.
    return response.data.items.sort((one, two) => {
        if (one.group instanceof Date && two.group instanceof Date) {
            return compareAsc(one.group, two.group);
        }

        return 0;
    });
};

/**
 * Count the number of interactions matching some conditions.
 * @param parameters Route filters.
 * @param options    Query options.
 * @returns          React Query result.
 */
const useInteractionsCounts = <T = IInteractionsCount<any>[]>(
    parameters: IInteractionsGetQueryParameters,
    options: Omit<UseQueryOptions<IInteractionsCount<any>[], AxiosError, T>, "queryKey" | "queryFn"> = {}
) => {
    // Grouping queries require the user's time zone. If the request is just for a single data point the time zone is
    // not strictly needed, but since the time zone is almost certainly available already in the query cache requesting
    // it here is not a big deal.
    const { data: timeZone } = useTimeZone();
    const hasTimeZone = typeof timeZone === "string";

    // Don't do the next request until the time zone is available and/or the options passed says the query is enabled.
    const queryOptions = { ...options, enabled: hasTimeZone && (options.enabled ?? true) };

    const url = Route.InteractionsCounts;
    const queryParameters = { ...parameters, timeZone };
    return useQuery<IInteractionsCount<any>[], AxiosError, T>(
        [url, queryParameters],
        () => getInteractionsCounts(url, queryParameters),
        queryOptions
    );
};

/**
 * Count the number of interactions matching some conditions but only return the first result which is common when not
 * doing a group by query.
 * @param parameters Route filters.
 * @param options    Query options.
 * @returns          React Query result.
 */
export const useInteractionsCount = (
    parameters: IInteractionsGetQueryParameters,
    options: Omit<UseQueryOptions<IInteractionsCount<null>[], AxiosError, number>, "queryKey" | "queryFn"> = {}
) => useInteractionsCounts(parameters, { ...options, select: (data) => data[0].count });

/**
 * Count the number of interactions with all selected tags
 * @param selectedTagIds    All selected tag Ids
 * @param parameters        Route filters.
 * @param options           Query options.
 */
export const useInteractionsMultipleCounts = (
    selectedTagIds: { [index: string]: string[] },
    parameters: IInteractionsGetQueryParameters,
    options: Omit<UseQueryOptions<IInteractionsCount<null>[], AxiosError, number>, "queryKey" | "queryFn"> = {}
) => {
    const { data: timeZone } = useTimeZone();
    const hasTimeZone = typeof timeZone === "string";

    // Don't do the next request until the time zone is available and/or the options passed says the query is enabled.
    const queryOptions = {
        ...options,
        enabled: hasTimeZone && (options.enabled ?? true),
    };

    const url = Route.InteractionsCounts;
    return useQueries(
        Object.keys(selectedTagIds).map((tagId) => {
            const enableMultipleCounts =
                selectedTagIds[tagId].length > 0 && selectedTagIds[tagId].every((tagId) => !isValueEmpty(tagId));

            const queryParameters = {
                ...parameters,
                timeZone,
                tagId: enableMultipleCounts ? selectedTagIds[tagId] : undefined,
            };

            return {
                queryKey: [url, queryParameters, tagId],
                queryFn: () => {
                    if (enableMultipleCounts) {
                        return getInteractionsCounts(url, queryParameters);
                    }
                    return doNothing();
                },
            };
        }, queryOptions)
    );
};

export default useInteractionsCounts;
